/**
 * Asset manifest types. Gameplay code only ever references AssetIds –
 * whether an id resolves to a greybox primitive or an exported GLB is decided
 * here, so swapping a placeholder for the final model never touches gameplay.
 * See docs/asset-specs/ASSET_PIPELINE.md.
 */
export type AssetId = string;

export const AssetCategory = {
  TrackModule: 'track-module',
  Obstacle: 'obstacle',
  Prop: 'prop',
  Vehicle: 'vehicle',
  Collectible: 'collectible',
  Environment: 'environment',
  Building: 'building',
  Character: 'character',
  Advertising: 'advertising',
  Effect: 'effect',
} as const;
export type AssetCategory = (typeof AssetCategory)[keyof typeof AssetCategory];

/** Required id prefix per category (see NAMING_CONVENTIONS.md). */
export const CATEGORY_PREFIX: Readonly<Record<AssetCategory, string>> = {
  'track-module': 'SEG',
  obstacle: 'OBS',
  prop: 'PROP',
  vehicle: 'VEH',
  collectible: 'COL',
  environment: 'ENV',
  building: 'BLD',
  character: 'CHAR',
  advertising: 'AD',
  effect: 'FX',
};

/** Folder below /assets/models/ per category. */
export const CATEGORY_MODEL_FOLDER: Readonly<Record<AssetCategory, string>> = {
  'track-module': 'track-modules',
  obstacle: 'obstacles',
  prop: 'props',
  vehicle: 'vehicles',
  collectible: 'collectibles',
  environment: 'environment',
  building: 'buildings',
  character: 'characters',
  advertising: 'advertising',
  effect: 'effects',
};

export const GreyboxMaterialId = {
  Track: 'track',
  TrackSide: 'trackSide',
  LaneLine: 'laneLine',
  Curb: 'curb',
  Fence: 'fence',
  FencePost: 'fencePost',
  Barrier: 'barrier',
  Chevron: 'chevron',
  Concrete: 'concrete',
  Wood: 'wood',
  Steel: 'steel',
  Container: 'container',
  ContainerAlt: 'containerAlt',
  Bottle: 'bottle',
  BottleCap: 'bottleCap',
  Golden: 'golden',
  Crate: 'crate',
  Heart: 'heart',
  Magnet: 'magnet',
  TimeBonus: 'timeBonus',
  WorkerVest: 'workerVest',
  WorkerShirt: 'workerShirt',
  WorkerPants: 'workerPants',
  WorkerHelmet: 'workerHelmet',
  Skin: 'skin',
  // Enclosed modules (tunnel, interior, shell building – Phase 3)
  Lamp: 'lamp',
  Water: 'water',
  ConcreteDark: 'concreteDark',
  Drywall: 'drywall',
  // Machines (VEH_DOZER_A greybox): paint, rubber, the scrolling rubber belt
  MachineYellow: 'machineYellow',
  RubberBlack: 'rubberBlack',
  DozerTrack: 'dozerTrack',
  Missing: 'missing',
} as const;
export type GreyboxMaterialId = (typeof GreyboxMaterialId)[keyof typeof GreyboxMaterialId];

export type GreyboxShape = 'box' | 'cylinder' | 'sphere' | 'capsule' | 'arcBox';

/**
 * Bent box along a curve module's centre line (SEG_15/16 greybox): the part is given in
 * path coordinates – position = [lateral, center y, s of the center], size = [width, height,
 * length along the centre line] – and swept along the arc. Rotation is ignored.
 */
export interface GreyboxArc {
  /** Curvature of the module's centre line (1/m, + = right turn), see segmentPath.ts. */
  readonly curvature: number;
}

/** One primitive of a greybox placeholder. Units: meters, rotation: degrees. */
export interface GreyboxPart {
  readonly shape: GreyboxShape;
  /** box: [w, h, d]; cylinder/capsule: [diameter, height, diameter]; sphere: [d, d, d]; arcBox: [w, h, path length]. */
  readonly size: readonly [number, number, number];
  readonly position: readonly [number, number, number];
  readonly rotation?: readonly [number, number, number];
  readonly material: GreyboxMaterialId;
  /** arcBox only. */
  readonly arc?: GreyboxArc;
  /** Rig node the part moves with (rigged greyboxes, GreyboxRigNode) – default: the root. */
  readonly node?: string;
  /**
   * Belt UVs instead of meter UVs (a dozer's rubber track): v runs along z, one unit per `bandTile` meters, u across –
   * the same contract as the GLB's belt (DozerConfig.vehicle.trackTileLength), so the game scrolls both alike.
   */
  readonly bandTile?: number;
}

/**
 * Named node of a rigged greybox – a vehicle whose parts the game moves by node name (VEH_DOZER_A: blade pivot,
 * cylinders, belts, wheels, sockets). Same names as the rig of its GLB, so one presenter drives both.
 */
export interface GreyboxRigNode {
  readonly name: string;
  /** Parent rig node (default: the root). Parents come first in the list. */
  readonly parent?: string;
  /** Rest position in asset space (meters) – the parts keep their asset-space positions. */
  readonly position: readonly [number, number, number];
  /** Rest rotation in asset space (degrees, Babylon's yaw-pitch-roll order like greybox parts). */
  readonly rotation?: readonly [number, number, number];
}

export type AssetSource =
  | { readonly kind: 'greybox'; readonly parts: readonly GreyboxPart[]; readonly rig?: readonly GreyboxRigNode[] }
  | {
      readonly kind: 'glb';
      /** Public URL, e.g. /assets/models/vehicles/veh_forklift_a.glb */
      readonly url: string;
      /** Greybox shown if the GLB is not loaded yet / failed to load. */
      readonly fallback?: readonly GreyboxPart[];
      /** Rig nodes of the fallback greybox (its parts with a `node`). */
      readonly fallbackRig?: readonly GreyboxRigNode[];
    };

export interface AssetLod {
  /** 1 = first reduced level. LOD0 is the main source. */
  readonly level: number;
  readonly url: string;
  /** Switch distance in meters. */
  readonly distance: number;
}

export type AssetCollision =
  /** No gameplay collision (decoration, ad carriers). */
  | { readonly kind: 'none' }
  /** Colliders are defined in gameplay data (ObstacleDefinition etc.) – authoritative. */
  | { readonly kind: 'gameplay-data' }
  /** Reserved: simplified proxy meshes inside the GLB (nodes prefixed COLLIDER_). */
  | { readonly kind: 'proxy-nodes'; readonly nodePrefix: string };

/** critical = before the menu, run = before a run starts, lazy = on first use. */
export type PreloadPolicy = 'critical' | 'run' | 'lazy';

export type AssetStatus = 'greybox' | 'blockout' | 'wip' | 'final';

export interface AssetMetadata {
  readonly description: string;
  readonly status: AssetStatus;
  /** Triangle budget for LOD0 (asset spec). */
  readonly triangleBudget?: number;
  readonly textureBudgetPx?: number;
  /** Max. materials (≈ draw calls per instance batch) – pipeline validation. */
  readonly materialBudget?: number;
  /** Max. GLB size in bytes incl. embedded textures – pipeline validation. */
  readonly byteBudget?: number;
  /** Relative path of the Blender source file (documentation only, never loaded). */
  readonly blenderSource?: string;
  readonly tags?: readonly string[];
}

export interface AssetManifestEntry {
  readonly id: AssetId;
  readonly category: AssetCategory;
  readonly source: AssetSource;
  readonly lods?: readonly AssetLod[];
  readonly collision: AssetCollision;
  readonly preload: PreloadPolicy;
  /**
   * instances = GPU-friendly hardware instances (static props, collectibles);
   * clones = independent copies (skinned/animated characters, dynamic events).
   */
  readonly instancing: 'instances' | 'clones';
  readonly metadata: AssetMetadata;
}
